import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { removeNotification } from '../../store/slices/notificationSlice';
import { FaCheckCircle, FaExclamationCircle, FaExclamationTriangle, FaInfoCircle, FaTimes } from 'react-icons/fa';

const NotificationToast = ({ notification, onClose }) => {
  const { id, type, title, message, duration = 5000 } = notification;

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <FaCheckCircle className="w-5 h-5 text-green-400" />;
      case 'error':
        return <FaExclamationCircle className="w-5 h-5 text-red-400" />;
      case 'warning':
        return <FaExclamationTriangle className="w-5 h-5 text-yellow-400" />;
      case 'info':
      default:
        return <FaInfoCircle className="w-5 h-5 text-blue-400" />;
    }
  };

  const getTypeClass = () => {
    switch (type) {
      case 'success':
        return 'notification-success';
      case 'error':
        return 'notification-error';
      case 'warning':
        return 'notification-warning';
      case 'info':
      default:
        return 'notification-info';
    }
  };

  return (
    <div className={`notification-toast ${getTypeClass()} mb-4 animate-slide-in shadow-2xl`}>
      <div className="p-5">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-4 flex-1 min-w-0">
            <p className="text-base font-semibold text-gray-900">{title}</p>
            <p className="mt-2 text-sm text-gray-700 leading-relaxed">{message}</p>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
              onClick={() => onClose(id)}
            >
              <span className="sr-only">Fermer</span>
              <FaTimes className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationSystem = () => {
  const dispatch = useDispatch();
  const { notifications, settings } = useSelector(state => state.notifications);

  const handleClose = (id) => {
    dispatch(removeNotification(id));
  };

  if (!settings.enableToast || notifications.length === 0) {
    return null;
  }

  const getPositionClass = () => {
    switch (settings.position) {
      case 'top-left':
        return 'top-0 left-0';
      case 'top-right':
        return 'top-0 right-0';
      case 'bottom-left':
        return 'bottom-0 left-0';
      case 'bottom-right':
        return 'bottom-0 right-0';
      default:
        return 'top-0 right-0';
    }
  };

  return (
    <div className={`fixed ${getPositionClass()} p-6 space-y-4 pointer-events-none`} style={{ zIndex: 10000 }}>
      <div className="pointer-events-auto">
        {notifications.slice(0, 5).map((notification) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onClose={handleClose}
          />
        ))}
      </div>
    </div>
  );
};

export default NotificationSystem;
